import { action, computed, makeObservable, observable, runInAction } from "mobx";
import { inject, injectable } from "inversify";
import type { UserType } from "@whatslly/types/User";
import type { LoginProviderType } from "@whatslly/types/LoginProviderType";
import type { GetUserAlertResponseType, UserAlertsType } from "@whatslly/types/integrations/schemas/user-alerts/GetUserAlertsResponseType";
import type { ModifyUserAlertBodyType, ModifyUserAlertResponseType } from "@whatslly/types/integrations/schemas/user-alerts/ModifyUserAlertsResponseType";
import type { ILoginData, IUserStore } from "@whatslly/ui-components/services/UserStore";
import { ContentCommands } from "@whatslly/commons/contentCommands";
import ms from "ms";
import type { TFunction } from "i18next";
import { AUTH_RESPONSE_ERROR_TYPES } from "@whatslly/types/AuthResponse";
import { WidgetCommands } from "@whatslly/commons/widgetCommands";
import { BgCommands } from "@whatslly/commons/bgCommands";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import { AuthStore } from "@whatslly/widget/stores/AuthStore";
import { WebScriptCommands } from "@whatslly/commons/webScriptCommands";
import { QUERY_RESPONSE_ERROR_TYPES } from "@whatslly/types/QueryResponse";
import { openEtokenExpiredModal } from "@whatslly/ui-components/modals/EtokenExpiredModal";
import { FieldStore } from "@whatslly/widget/stores/FieldStore";
import { NotificationsStore } from "@whatslly/widget/stores/NotificationsStore";
import type { IStorage } from "@whatslly/commons/storage.constants";
import { InjectStorage } from "@whatslly/commons/storage.constants";
import { LoginCommands } from "@whatslly/commons/loginCommands";
import { loginListener } from "@whatslly/commons/loginListener";
import { webScriptClient } from "@whatslly/instant-messaging/webScriptClient";
import { backgroundClient } from "@whatslly/commons/background/client";
import { randomString } from "@whatslly/commons/utils";
import { canUserLoginToTwilioSms, canUserLoginToWaba } from "@whatslly/commons/userMobileUtils";
import { modalService } from "@whatslly/ui-components/modals";
import Toast from "@whatslly/ui-components/v2/Toaster/Toast";

const WAIT_TIMEOUT = ms("60sec");

const buildLogoutTextMessage = (t: TFunction, isDesktopApp: boolean | undefined) => {
  if (!t || typeof isDesktopApp === "undefined") {
    return;
  }
  return isDesktopApp ? t("etoken_expired_modal.logout") : t("etoken_expired_modal.continue_with_out_tuvis");
};

@injectable()
export class UserStore implements IUserStore {
  private _user?: UserType;
  phone?: string;
  pod?: string;
  extensionVersion?: string;
  loginProvider: LoginProviderType;
  userAlerts: UserAlertsType | undefined;
  protected logger?: FrontLogger;
  desktopVersion?: string | undefined;
  maskedEliteToken?: string | undefined;
  isReconnect = false;

  constructor(
    @inject(FrontLogger) private loggerService: FrontLogger,
    @inject(AuthStore) protected authStore: AuthStore,
    @inject(FieldStore) protected fieldStore: FieldStore,
    @inject(NotificationsStore) protected notificationsStore: NotificationsStore,
    @inject(InjectStorage) protected storage: IStorage
  ) {
    this.logger = loggerService.getLogger("user-store");
    makeObservable(this, {
      _user: observable,
      phone: observable,
      pod: observable,
      userAlerts: observable,
      loginProvider: observable,
      extensionVersion: observable,
      desktopVersion: observable,
      setPhone: action,
      setDesktopVersion: action,
      user: computed,
      modifyAlert: action,
      maskedEliteToken: observable,
      updateUserData: action,
      //eslint-disable-next-line @typescript-eslint/no-explicit-any
    } as any);
  }

  get canUserLoginToTwilioSms() {
    return canUserLoginToTwilioSms(this.user);
  }

  get canUserLoginToWaba() {
    return canUserLoginToWaba(this.user);
  }

  get user() {
    if (this._user?.query_cache_ttl_nightly && this.desktopVersion?.includes("-rc")) {
      this._user.query_cache_ttl = this._user.query_cache_ttl_nightly;
    }
    return this._user;
  }

  set user(user: UserType | undefined) {
    this._user = user;
  }

  async login(data: ILoginData, t: TFunction): Promise<void | { navigate: string; state?: string[] }> {
    let timeoutId: NodeJS.Timeout | null = null;
    try {
      const res = await new Promise<void | { navigate: string; state?: string[] }>((resolve, reject) => {
        const transactionId = randomString(20);
        backgroundClient.sendSync({
          cmd: BgCommands.SEND_TO_REDIRECT,
          transactionId,
          data: {
            email: data.email,
            domain: data.customDomain,
            userPhone: this.phone,
            type: data.app,
            chatApp: "popup",
            loginSource: "widget",
          },
        });
        const unsubscribe = loginListener.on(LoginCommands.LOGIN_COMPLETED, async (data) => {
          if (data.transactionId !== transactionId) return;
          unsubscribe();
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
          if (data?.error) {
            reject(data.error);
            return;
          }
          try {
            await backgroundClient.send(BgCommands.GET_APPS_TOKEN, {
              code: data?.code,
              state: data?.state,
              error: data?.error,
              errorDescription: data?.error_description,
              timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
              requestId: randomString(20),
            });
            resolve(undefined);
          } catch (err: any) {
            if (err?.message === AUTH_RESPONSE_ERROR_TYPES.PHONE_NOT_MATCH) {
              return resolve({ navigate: "/phone-not-match-error", state: err.data });
            }
            reject(err?.message ?? t("auth_failed"));
          }
        });

        timeoutId = setTimeout(() => {
          unsubscribe();
          reject(t("auth_failed"));
        }, WAIT_TIMEOUT);
      });

      const { active, loggedIn, pod, extensionVersion, loginProvider, user } = await backgroundClient.send(BgCommands.CHECK_IF_EXT_ACTIVE);

      if (loggedIn) {
        window.postMessage({
          cmd: ContentCommands.AUTH,
          data: { active, loggedIn, pod, extensionVersion, loginProvider, user },
        });
      }

      return res;
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  }

  async refreshUser(t: TFunction, isDesktopApp?: boolean) {
    const modal = modalService.openModal(Toast, { text: t("search_person.loading"), timeout: 60 * 5 * 1000 });
    try {
      window.postMessage({ cmd: WidgetCommands.SHOW_LOADING }, "*");
      const loginSource = isDesktopApp ? "desktop" : "widget";
      const res = await backgroundClient.send(BgCommands.REFRESH_USER, { userPhone: this.phone, loginSource });
      if (res?.success) {
        this.user = res.user;
        this.pod = res.wPod;
        this.maskedEliteToken = res.maskedEliteToken;
        if (res.fields) {
          this.fieldStore.forceResetData();
        }
        webScriptClient.send(WebScriptCommands.USER_REFRESHED, { user: res.user });
        backgroundClient.send(BgCommands.ANALYTICS_TRACK, {
          schemaName: `user_interaction`,
          schemaContent: {
            interaction_name: "refresh-user",
          },
        });
      }
      if (
        !res?.success &&
        (res?.error === QUERY_RESPONSE_ERROR_TYPES.USER_WITHOUT_PERMISSION || (res?.errorCode && ["INVALID_SESSION_ID"].includes(res.errorCode)))
      ) {
        const logoutText = buildLogoutTextMessage(t, isDesktopApp);
        const loggedIn = await openEtokenExpiredModal(this, logoutText);
        if (loggedIn) {
          this.refreshUser(t, isDesktopApp);
          return;
        }
        this.logout();
      }
    } catch (err) {
      this.logger?.error({ err }, `failed to refresh user`);
    } finally {
      modal.close(undefined);
      window.postMessage({ cmd: WidgetCommands.HIDE_LOADING }, "*");
    }
  }

  async logout() {
    this.notificationsStore.reset();
    this.fieldStore.forceResetData();
    webScriptClient.send(WebScriptCommands.CHANGE_WAP_BLUR, { newState: false });
    this.authStore.logout();
    backgroundClient.send(BgCommands.TRK, { action: "sf-user-logged-out" });
    this._user = undefined;
    this.isReconnect = false;
  }

  setPhone(val: string) {
    this.phone = val;
  }

  async loadUserAlerts(): Promise<GetUserAlertResponseType> {
    const res = await backgroundClient.send<typeof BgCommands.HTTP_REQUEST, GetUserAlertResponseType>(BgCommands.HTTP_REQUEST, {
      urlSuffix: "/integrations/user-alerts",
      method: "GET",
    });

    if (res.success) {
      runInAction(() => (this.userAlerts = res.alerts));
    }

    return res;
  }

  async modifyAlert({ name, value }: ModifyUserAlertBodyType): Promise<ModifyUserAlertResponseType> {
    if (this.userAlerts) {
      this.userAlerts[name] = !value;
    }
    return await backgroundClient.send<typeof BgCommands.HTTP_REQUEST, ModifyUserAlertResponseType>(BgCommands.HTTP_REQUEST, {
      urlSuffix: "/integrations/user-alerts",
      method: "POST",
      body: JSON.stringify({ name, value }),
    });
  }

  setDesktopVersion(val: string) {
    this.desktopVersion = val;
  }

  updateUserData(newUserData: Partial<UserType>) {
    this._user = { ...this._user, ...newUserData } as UserType;
  }
}
